import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { statusLabel, fmtRelative, fmtVal } from "@/lib/format";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertTriangle, MapPin, Move } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useRefresh } from "@/context/RefreshContext";

const COLOR = { green: "#10b981", orange: "#f59e0b", red: "#ef4444", grey: "#94a3b8" };

function mapMarkerIcon(color, draggable) {
  return L.divIcon({
    className: "thrm-map-marker",
    html: `<span style="display:block;width:24px;height:24px;border-radius:9999px;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.45);cursor:${draggable ? "grab" : "pointer"}"></span>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
    tooltipAnchor: [0, -12],
  });
}

// MapContainer center/zoom are only used on mount; this keeps the view in sync
// when device coordinates change (live auto-refresh or returning to the page).
function Recenter({ points, disabled }) {
  const map = useMap();
  const key = points.map((p) => `${p[0]},${p[1]}`).join("|");
  useEffect(() => {
    if (disabled || !points.length) return;
    if (points.length === 1) {
      map.setView(points[0], Math.max(map.getZoom(), 11), { animate: true });
    } else {
      map.fitBounds(points, { padding: [40, 40] });
    }
  }, [key, disabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function MapPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const { tick } = useRefresh();
  const [devices, setDevices] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [manualView, setManualView] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/devices").then((r) => setDevices(r.data)).catch(() => setDevices([]));
  }, [tick]);

  const located = useMemo(() => (devices || []).filter((d) => d.latitude != null && d.longitude != null), [devices]);
  const center = located.length ? [located[0].latitude, located[0].longitude] : [52.1, 5.3];
  const attention = (devices || []).filter((d) => d.status_color === "red" || d.status_color === "orange").length;

  const tiles = theme === "dark"
    ? { url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", attr: "&copy; OpenStreetMap &copy; CARTO" }
    : { url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", attr: "&copy; OpenStreetMap &copy; CARTO" };

  const moveDevice = async (device, event) => {
    const previous = { latitude: device.latitude, longitude: device.longitude };
    const point = event.target.getLatLng();
    const latitude = Number(point.lat.toFixed(6));
    const longitude = Number(point.lng.toFixed(6));
    setDevices((current) => current.map((item) => item.id === device.id ? { ...item, latitude, longitude } : item));
    setSavingId(device.id);
    try {
      await api.patch(`/devices/${device.id}/location`, { latitude, longitude });
      toast.success(t(`Locatie van ${device.name} opgeslagen`, `Location of ${device.name} saved`));
    } catch (error) {
      setDevices((current) => current.map((item) => item.id === device.id ? { ...item, ...previous } : item));
      toast.error(formatApiError(error.response?.data?.detail) || t("Locatie kon niet worden opgeslagen", "Location could not be saved"));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight sm:text-4xl">{t("Kaart", "Map")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("Locaties van uw installaties met live status.", "Locations of your installations with live status.")}</p>
        </div>
        {attention > 0 && (
          <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-500">
            <AlertTriangle className="h-4 w-4" /> {t(`${attention} installatie(s) vragen aandacht`, `${attention} installation(s) need attention`)}
          </div>
        )}
      </div>

      {isAdmin && located.length > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-primary/25 bg-primary/5 px-3 py-2 text-sm text-muted-foreground" data-testid="map-admin-drag-help">
          <Move className="h-4 w-4 text-primary" />
          <span>{t("Beheer: sleep een locatiepin en laat los om de nieuwe positie op te slaan.", "Admin: drag a location marker and release it to save its new position.")}</span>
          {savingId && <span className="ml-auto text-xs text-primary">{t("Opslaan…", "Saving…")}</span>}
        </div>
      )}

      {devices === null ? (
        <Skeleton className="h-[560px] w-full rounded-lg" />
      ) : located.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-20 text-center">
          <MapPin className="h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 text-sm text-muted-foreground">{t("Nog geen apparaten met coördinaten. Voeg locatie toe in de beheeromgeving.", "No devices with coordinates yet. Add a location in the admin area.")}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border" style={{ height: 560 }} data-testid="device-map">
          <MapContainer center={center} zoom={7} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer url={tiles.url} attribution={tiles.attr} />
            <Recenter points={located.map((d) => [d.latitude, d.longitude])} disabled={manualView} />
            {located.map((d) => {
              const c = COLOR[d.status_color] || COLOR.grey;
              return (
                <Marker key={d.id} position={[d.latitude, d.longitude]}
                  icon={mapMarkerIcon(c, isAdmin)} draggable={isAdmin && savingId !== d.id}
                  eventHandlers={isAdmin ? {
                    dragstart: () => setManualView(true),
                    dragend: (event) => moveDevice(d, event),
                  } : undefined}
                  data-testid={`map-marker-draggable-${d.id}`}>
                  <Tooltip direction="top" offset={[0, -8]}>{d.name}</Tooltip>
                  <Popup>
                    <div style={{ minWidth: 180 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{d.name}</div>
                      <div style={{ color: "#64748b", fontSize: 12, marginBottom: 6 }}>{d.location || "—"}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                        <span style={{ height: 8, width: 8, borderRadius: 8, background: c, display: "inline-block" }} />
                        <b>{d.device_type === "pressure_monitor" ? (d.status_color === "orange" ? t("Werkdruk overschreden", "Working pressure exceeded") : t("Pressure Monitor", "Pressure Monitor")) : statusLabel(d.status_color, lang)}</b> · {d.online ? "Online" : "Offline"}
                      </div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>{t("Laatste waarde", "Last value")}: <b>{d.device_type === "pressure_monitor" ? (d.samples || []).map(s => `${s.channel_id}: ${fmtVal(s.engineering_value)} bar`).join(" · ") || "—" : fmtVal(d.last_value)}</b></div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{fmtRelative(d.last_seen, lang)}</div>
                      <button onClick={() => navigate(`/devices/${d.id}`)}
                        style={{ marginTop: 8, width: "100%", background: "#f97316", color: "#fff", border: "none", borderRadius: 6, padding: "6px 8px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                        Details bekijken
                      </button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      )}
    </div>
  );
}
